import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, blogCategoryApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import '../styles/admin-dashboard.css';

const POPULAR_BLOG_ICONS = ['🧠', '🌿', '💙', '💑', '✨', '🧘', '💼', '🌱', '📝', '🎯', '💭', '🌟', '📚', '🤝', '🩺', '🛡️', '⚡', '☕'];

function AdminBlogCategoriesPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '📝',
    displayOrder: 0,
    isActive: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete Confirmation Modal
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, category: null, loading: false });

  // Notification Toast / Alert
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      let res;
      try {
        res = await blogCategoryApi.adminGetCategories({ all: 'true' });
      } catch (adminErr) {
        console.warn('Admin categories endpoint fallback to public:', adminErr);
        res = await blogCategoryApi.getPublicCategories({ all: 'true' });
      }

      if (res?.success || res?.categories || Array.isArray(res)) {
        const list = res.categories || res.data || (Array.isArray(res) ? res : []);
        setCategories(list);
      }
    } catch (err) {
      console.error('Error fetching blog categories:', err);
      showFeedback('error', err.message || 'Failed to load blog categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchCategories();
  }, [fetchCategories, authSession?.token, navigate]);

  // Derived Dynamic Real-Time Statistics
  const totalCount = categories.length;
  const activeCount = categories.filter((c) => c.isActive !== false).length;
  const inactiveCount = categories.filter((c) => c.isActive === false).length;

  // Filtered list for table display
  const filteredCategories = categories.filter((cat) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (cat.name && cat.name.toLowerCase().includes(q)) ||
      (cat.slug && cat.slug.toLowerCase().includes(q)) ||
      (cat.description && cat.description.toLowerCase().includes(q));

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && cat.isActive !== false) ||
      (statusFilter === 'inactive' && cat.isActive === false);

    return matchesSearch && matchesStatus;
  });

  // Open Create Modal
  const handleOpenCreate = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '📝',
      displayOrder: categories.length + 1,
      isActive: true,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (cat) => {
    setModalMode('edit');
    setEditingId(cat._id || cat.id);
    setFormData({
      name: cat.name || '',
      slug: cat.slug || '',
      description: cat.description || '',
      icon: cat.icon || '📝',
      displayOrder: cat.displayOrder !== undefined ? cat.displayOrder : 0,
      isActive: cat.isActive !== undefined ? cat.isActive : true,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  // Auto-generate slug as user types name
  const handleNameChange = (e) => {
    const newName = e.target.value;
    const generatedSlug = newName
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

    setFormData((prev) => ({
      ...prev,
      name: newName,
      slug: modalMode === 'create' || !prev.slug ? generatedSlug : prev.slug,
    }));
  };

  // Submit Form (Create / Edit)
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Category name is required.');
      return;
    }

    try {
      setFormLoading(true);
      if (modalMode === 'create') {
        const res = await blogCategoryApi.adminCreateCategory(formData);
        showFeedback('success', res.message || `Blog category "${formData.name}" created successfully!`);
      } else {
        const res = await blogCategoryApi.adminUpdateCategory(editingId, formData);
        showFeedback('success', res.message || `Blog category "${formData.name}" updated successfully!`);
      }
      setIsModalOpen(false);
      fetchCategories();
    } catch (err) {
      console.error('Error saving blog category:', err);
      setFormError(err.message || 'Failed to save blog category.');
    } finally {
      setFormLoading(false);
    }
  };

  // Toggle Category Active Status
  const handleToggleActive = async (cat) => {
    const id = cat._id || cat.id;
    try {
      const res = await blogCategoryApi.adminToggleCategory(id);
      showFeedback('success', res.message || `Category "${cat.name}" status updated.`);
      setCategories((prev) =>
        prev.map((c) => ((c._id || c.id) === id ? { ...c, isActive: !c.isActive } : c))
      );
    } catch (err) {
      console.error('Error toggling category status:', err);
      showFeedback('error', err.message || 'Failed to update category status.');
    }
  };

  // Confirm and Execute Delete
  const handleConfirmDelete = async () => {
    if (!deleteModal.category) return;
    const id = deleteModal.category._id || deleteModal.category.id;
    const catName = deleteModal.category.name;

    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      const res = await blogCategoryApi.adminDeleteCategory(id);
      showFeedback('success', res.message || `Blog category "${catName}" deleted successfully.`);
      setCategories((prev) => prev.filter((c) => (c._id || c.id) !== id));
      setDeleteModal({ isOpen: false, category: null, loading: false });
    } catch (err) {
      console.error('Error deleting blog category:', err);
      showFeedback('error', err.message || 'Failed to delete blog category.');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="admin-layout" style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
      <DashboardSidebar userRole="admin" />

      <div style={{ flex: 1, marginLeft: '260px', padding: '32px', maxWidth: '1400px', width: 'calc(100% - 260px)' }}>
        {/* Top Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <span style={{ fontSize: '28px' }}>📚</span>
              <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                Blog Categories Management
              </h1>
            </div>
            <p style={{ margin: 0, color: '#64748b', fontSize: '15px' }}>
              Create, organize, and manage blog categories used across therapist & admin blog publishing forms.
            </p>
          </div>

          <button
            onClick={handleOpenCreate}
            style={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(79, 70, 229, 0.35)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <span>➕</span>
            <span>Add New Category</span>
          </button>
        </div>

        {/* Feedback Alert */}
        {feedback.message && (
          <div
            style={{
              padding: '14px 18px',
              borderRadius: '10px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: feedback.type === 'success' ? '#ecfdf5' : '#fef2f2',
              color: feedback.type === 'success' ? '#047857' : '#b91c1c',
              border: `1px solid ${feedback.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{feedback.type === 'success' ? '✅' : '⚠️'}</span>
              <span>{feedback.message}</span>
            </div>
            <button
              onClick={() => setFeedback({ type: '', message: '' })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Real-time Statistics Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Blog Categories</span>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>{totalCount}</div>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>All categories in library</span>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active Categories</span>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#059669', marginTop: '6px' }}>{activeCount}</div>
            <span style={{ fontSize: '12px', color: '#10b981' }}>Visible in blog insertion forms</span>
          </div>

          <div style={{ background: 'white', padding: '20px', borderRadius: '14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Inactive / Hidden</span>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#d97706', marginTop: '6px' }}>{inactiveCount}</div>
            <span style={{ fontSize: '12px', color: '#f59e0b' }}>Disabled from blog forms</span>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div style={{ background: 'white', padding: '16px 20px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: '260px', position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search blog categories by name, slug, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff', color: '#334155', fontWeight: 600 }}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>

        {/* Categories Table View */}
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <div style={{ fontSize: '16px', fontWeight: 600 }}>Loading blog categories...</div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', color: '#64748b' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>📂</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>No Blog Categories Found</div>
            <p style={{ margin: '0 0 18px 0', fontSize: '14px' }}>
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search query or status filter.'
                : 'Get started by creating your first blog category.'}
            </p>
            <button
              onClick={handleOpenCreate}
              style={{
                padding: '10px 20px',
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              ➕ Create Blog Category
            </button>
          </div>
        ) : (
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 700 }}>
                  <th style={{ padding: '14px 20px', width: '70px', textAlign: 'center' }}>Icon</th>
                  <th style={{ padding: '14px 20px' }}>Category Name</th>
                  <th style={{ padding: '14px 20px' }}>Slug</th>
                  <th style={{ padding: '14px 20px' }}>Description</th>
                  <th style={{ padding: '14px 20px', textAlign: 'center' }}>Order</th>
                  <th style={{ padding: '14px 20px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((cat) => {
                  const id = cat._id || cat.id;
                  const isActive = cat.isActive !== false;
                  return (
                    <tr key={id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '16px 20px', textAlign: 'center', fontSize: '22px' }}>
                        {cat.icon || '📝'}
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>
                          {cat.name}
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <code style={{ background: '#f1f5f9', color: '#4f46e5', padding: '3px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                          {cat.slug}
                        </code>
                      </td>
                      <td style={{ padding: '16px 20px', color: '#64748b', maxWidth: '280px' }}>
                        {cat.description || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>No description</span>}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 700, color: '#64748b' }}>
                        {cat.displayOrder || 0}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleToggleActive(cat)}
                          title="Click to toggle status"
                          style={{
                            border: 'none',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: isActive ? '#dcfce7' : '#fee2e2',
                            color: isActive ? '#15803d' : '#b91c1c',
                            transition: 'all 0.2s',
                          }}
                        >
                          {isActive ? '● Active' : '○ Inactive'}
                        </button>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => handleOpenEdit(cat)}
                            style={{
                              background: '#f1f5f9',
                              color: '#334155',
                              border: '1px solid #cbd5e1',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => setDeleteModal({ isOpen: true, category: cat, loading: false })}
                            style={{
                              background: '#fef2f2',
                              color: '#dc2626',
                              border: '1px solid #fecaca',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ==================== CREATE / EDIT MODAL ==================== */}
        {isModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f8fafc',
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>{modalMode === 'create' ? '➕' : '✏️'}</span>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                    {modalMode === 'create' ? 'Create New Blog Category' : 'Edit Blog Category'}
                  </h3>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              {/* Form Body */}
              <form onSubmit={handleSubmitForm} style={{ padding: '24px' }}>
                {formError && (
                  <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '18px', fontSize: '13px', fontWeight: 600 }}>
                    ⚠️ {formError}
                  </div>
                )}

                {/* Category Name & Icon Picker */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Category Name *
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ width: '60px', position: 'relative' }}>
                      <input
                        type="text"
                        value={formData.icon}
                        onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                        maxLength={4}
                        title="Category Icon (Emoji)"
                        style={{ width: '100%', padding: '10px', textAlign: 'center', fontSize: '20px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                      />
                    </div>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Anxiety & Stress"
                      value={formData.name}
                      onChange={handleNameChange}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Icon Quick Select Palette */}
                <div style={{ marginBottom: '16px' }}>
                  <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                    Quick Select Icon:
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {POPULAR_BLOG_ICONS.map((emoji) => (
                      <button
                        type="button"
                        key={emoji}
                        onClick={() => setFormData({ ...formData, icon: emoji })}
                        style={{
                          background: formData.icon === emoji ? '#e0e7ff' : '#f8fafc',
                          border: `1px solid ${formData.icon === emoji ? '#6366f1' : '#e2e8f0'}`,
                          borderRadius: '6px',
                          fontSize: '18px',
                          padding: '6px 8px',
                          cursor: 'pointer',
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                {/* URL Slug */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    URL Slug
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="e.g. anxiety-stress"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', background: '#f8fafc' }}
                  />
                  <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                    Generated automatically from name. Used in URLs and filters.
                  </span>
                </div>

                {/* Description */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Description (Optional)
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description about the topic covered in this blog category..."
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                </div>

                {/* Display Order & Active Toggle */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                      Display Order (Sort)
                    </label>
                    <input
                      type="number"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '18px' }}>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        style={{ width: '18px', height: '18px', accentColor: '#4f46e5', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                        Active & Available in Blog Forms
                      </span>
                    </label>
                  </div>
                </div>

                {/* Form Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #e2e8f0', paddingTop: '18px' }}>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    style={{ padding: '10px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    style={{
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 700,
                      cursor: formLoading ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                    }}
                  >
                    {formLoading ? 'Saving...' : modalMode === 'create' ? 'Create Category' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ==================== DELETE CONFIRMATION MODAL ==================== */}
        {deleteModal.isOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px',
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                maxWidth: '440px',
                width: '100%',
                padding: '24px',
                textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                Delete Blog Category?
              </h3>
              <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>
                Are you sure you want to delete category <strong>"{deleteModal.category?.name}"</strong>? This will remove it from blog publishing choices.
              </p>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={() => setDeleteModal({ isOpen: false, category: null, loading: false })}
                  style={{ padding: '10px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteModal.loading}
                  style={{
                    padding: '10px 22px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 700,
                    cursor: deleteModal.loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBlogCategoriesPage;
